import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from '../styles/NavColumn.module.css';
import GroupsIcon from '../images/GroupsIcon.png';
import NotificationsIcon from '../images/NotificationsIcon.png';
import ExploreIcon from '../images/ExploreIcon.png';
import PlusIcon from '../images/PlusIcon.png';
import EventsIcon from '../images/EventsIcon.png';
import { trpc } from '../utils/trpc';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useRef, useState } from 'react';
import BackIcon from '../images/BackIcon.png';

export function NavColumn() {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { pathname } = useLocation();
  const { isVenueOwner, isMember, userType, isAuthenticated, isLoading } = useAuth();
  const [createMenu, setCreateMenu] = useState(false);

  const { data: memberProfile } = trpc.member.getMyProfile.useQuery(undefined, {
    enabled: !isLoading && isAuthenticated && isMember,
    staleTime: 0,
  });
  const { data: ownerProfile } = trpc.venueOwner.getMyProfile.useQuery(undefined, {
    enabled: !isLoading && isAuthenticated && isVenueOwner,
    staleTime: 0,
  });

  const { data: unreadNotificationsCount } = trpc.notification.getUnreadCount.useQuery(
    undefined,
    {
      enabled: !isLoading && isAuthenticated,
      refetchInterval: 30000, // refetch every 30s
    },
  );

  const profilePictureUrl = isMember
    ? memberProfile?.profile_picture_url
    : ownerProfile?.profile_picture_url;

  // Navigate to profile depending on account type
  const navigateProfile = async () => {
    if (!userType) {
      navigate('/sign-in');
      return;
    }

    if (isMember) {
      navigate('/group-member/profile');
    } else if (isVenueOwner) {
      navigate('/venue-owner/profile');
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!menuRef.current) return;

      if (!menuRef.current.contains(e.target as Node)) {
        setCreateMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div id={styles.NavColumn}>
      <div id={styles.Bubble}>
        <div className={`${styles.anticircle} ${styles.c1}`}></div>
        <div className={`${styles.anticircle} ${styles.c2}`}></div>
        <div className={`${styles.anticircle} ${styles.c3}`}></div>
        <div className={`${styles.anticircle} ${styles.c4}`}></div>
        <div className={`${styles.anticircle} ${styles.c5}`}></div>
        <div className={`${styles.circle} ${styles.c1}`}></div>
        <div className={`${styles.circle} ${styles.c2}`}></div>
        <div className={`${styles.circle} ${styles.c3}`}></div>
        <div className={`${styles.circle} ${styles.c4}`}></div>
        <div className={`${styles.circle} ${styles.c5}`}>
          <button onClick={() => navigate(-1)}>
            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={BackIcon}
              alt="Back Button"
              className={styles.backButton}
            ></img>
          </button>
        </div>

        <div className={styles.accountButton}>
          {profilePictureUrl ? (
            <button onClick={navigateProfile}>
              <img
                onLoad={(e) => e.currentTarget.classList.add('loaded')}
                src={profilePictureUrl}
                alt="Account Icon"
                className={styles.accountIcon}
              />
            </button>
          ) : (
            <button onClick={navigateProfile}>
              <div className={`${styles.accountIcon} ${styles.default}`}>
                {(
                  memberProfile?.first_name ||
                  memberProfile?.username ||
                  ownerProfile?.username ||
                  ownerProfile?.first_name ||
                  '?'
                )
                  .charAt(0)
                  .toUpperCase()}
              </div>
            </button>
          )}
        </div>
      </div>

      <nav className={styles.nav}>
        <Link
          to="/explore"
          className={`${styles.navButton} ${pathname.startsWith('/explore') ? styles.active : styles.inactive}`}
        >
          <span className={styles.dot} />
          <img
            onLoad={(e) => e.currentTarget.classList.add('loaded')}
            src={ExploreIcon}
            alt="Explore Icon"
            className={styles.navIcon}
          />
        </Link>
        <Link
          to="/notifications"
          className={`${styles.navButton} ${pathname.startsWith('/notifications') ? styles.active : styles.inactive}`}
        >
          <span className={styles.dot} />
          <div className={styles.iconWrapper}>
            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={NotificationsIcon}
              alt="Notifications Icon"
              className={styles.navIcon}
            />
            {unreadNotificationsCount !== undefined && unreadNotificationsCount > 0 && (
              <span className={styles.notificationBadge}>
                {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
              </span>
            )}
          </div>
        </Link>
        <Link
          to="/social"
          className={`${styles.navButton} ${pathname.startsWith('/social') ? styles.active : styles.inactive}`}
        >
          <span className={styles.dot} />
          <img
            onLoad={(e) => e.currentTarget.classList.add('loaded')}
            src={GroupsIcon}
            alt="Social Icon"
            className={styles.navIcon}
          />
        </Link>
        <div className={styles.createMenuContainer}>
          <button
            className={`${styles.navButton}  ${styles.createButton} ${pathname.startsWith('/create') ? styles.active : styles.inactive}`}
            onClick={() => setCreateMenu(!createMenu)}
          >
            <span className={styles.dot} />

            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={PlusIcon}
              className={`${styles.plusIcon} ${createMenu ? styles.white : ''} `}
              alt="Create New"
            ></img>
          </button>

          <div
            ref={menuRef}
            className={`${styles.createMenu} ${
              createMenu ? styles.createMenuOpen : styles.createMenuClosed
            }`}
          >
            <Link to={'/create-group'} onClick={() => setCreateMenu(false)}>
              <img
                onLoad={(e) => e.currentTarget.classList.add('loaded')}
                src={GroupsIcon}
              ></img>
            </Link>

            <Link to="/" onClick={(e) => e.preventDefault()}>
              <img
                onLoad={(e) => e.currentTarget.classList.add('loaded')}
                src={EventsIcon}
              ></img>
            </Link>
          </div>
        </div>
      </nav>
    </div>
  );
}
